/**
 * Utility per gestire il consenso cookie conforme GDPR
 */

export type CookieCategory = 'necessary' | 'analytics' | 'functional' | 'marketing';

export interface CookieConsent {
  necessary: boolean; // Sempre true, non modificabile
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
  timestamp: number;
}

const COOKIE_CONSENT_KEY = 'cookie_consent';
const CONSENT_VERSION = '1.0';

/**
 * Consenso predefinito: solo cookie tecnici necessari
 */
export const DEFAULT_CONSENT: CookieConsent = {
  necessary: true,
  analytics: false,
  functional: false,
  marketing: false,
  timestamp: Date.now(),
};

/**
 * Tutti i cookie accettati
 */
export const ALL_ACCEPTED_CONSENT: CookieConsent = {
  necessary: true,
  analytics: true,
  functional: true,
  marketing: true,
  timestamp: Date.now(),
};

/**
 * Legge il consenso salvato nel localStorage
 */
export function loadCookieConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!saved) return null;
    
    const parsed = JSON.parse(saved);
    
    // Verifica che abbia la struttura corretta
    if (parsed && typeof parsed === 'object' && 'necessary' in parsed) {
      return {
        ...parsed,
        necessary: true, // Forza sempre true per cookie necessari
      };
    }
    
    return null;
  } catch (error) {
    console.error('Errore nel caricamento delle preferenze cookie:', error);
    return null;
  }
}

/**
 * Salva il consenso nel localStorage
 */
export function saveCookieConsent(consent: CookieConsent): void {
  if (typeof window === 'undefined') return;
  
  try {
    const toSave = {
      ...consent,
      necessary: true, // Forza sempre true
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };
    
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(toSave));
    
    // Trigger evento custom per notificare i componenti
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
      detail: toSave 
    }));
  } catch (error) {
    console.error('Errore nel salvataggio delle preferenze cookie:', error);
  }
}

/**
 * Verifica se l'utente ha già dato il consenso
 */
export function hasGivenConsent(): boolean {
  return loadCookieConsent() !== null;
}

/**
 * Verifica se una categoria specifica è consentita
 */
export function isCategoryAllowed(category: CookieCategory): boolean {
  const consent = loadCookieConsent();
  
  // Se non c'è consenso salvato, solo i cookie necessari sono permessi
  if (!consent) {
    return category === 'necessary';
  }
  
  return consent[category] === true;
}

/**
 * Resetta il consenso (per testing o per ripulire)
 */
export function resetCookieConsent(): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
      detail: null 
    }));
  } catch (error) {
    console.error('Errore nel reset delle preferenze cookie:', error);
  }
}

/**
 * Blocca/sblocca script di terze parti in base al consenso
 */
export function updateThirdPartyScripts(consent: CookieConsent): void {
  if (typeof window === 'undefined') return;
  
  // Aggiorna Google Consent Mode v2
  updateGoogleConsentMode(consent);
}

/**
 * Aggiorna Google Consent Mode v2 con le preferenze utente
 */
function updateGoogleConsentMode(consent: CookieConsent): void {
  if (typeof window === 'undefined') return;
  
  // Usa gtag se disponibile
  if (window.gtag) {
    window.gtag('consent', 'update', {
      'analytics_storage': consent.analytics ? 'granted' : 'denied',
      'ad_storage': consent.marketing ? 'granted' : 'denied',
      'functionality_storage': consent.functional ? 'granted' : 'denied',
      'personalization_storage': consent.functional ? 'granted' : 'denied',
      'security_storage': 'granted',
    });
  }
  
  // Fallback con dataLayer
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'cookie_consent_update',
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      functionality_storage: consent.functional ? 'granted' : 'denied',
      personalization_storage: consent.functional ? 'granted' : 'denied',
      security_storage: 'granted',
    });
  }
  
  // Log per debug
  if (process.env.NODE_ENV === 'development') {
    console.log('🍪 Google Consent Mode aggiornato:', {
      analytics: consent.analytics ? 'granted' : 'denied',
      marketing: consent.marketing ? 'granted' : 'denied',
      functional: consent.functional ? 'granted' : 'denied',
    });
  }
}

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
    gtag?: (...args: any[]) => void;
  }
  
  interface WindowEventMap {
    cookieConsentUpdated: CustomEvent<CookieConsent | null>;
  }
}
